import { useState, useEffect } from "react";
import styles from "./MasonryGalleryPro.module.css";
import heroImg from '../../../images/gallery-plugin-2.png';
import ss1 from '../../../images/gallery-plugin-3.png';
import ss2 from '../../../images/gallery-plugin-4.png';
import ss3 from '../../../images/gallery-plugin-5.png';
import ss4 from '../../../images/gallery-plugin-6.png';
import ss5 from '../../../images/gallery-plugin.png';
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';

const layouts = [
  {
    id: "masonry",
    label: "Masonry",
    icon: (
      <svg viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="18" height="24" rx="3" fill="currentColor" opacity=".9"/>
        <rect x="24" y="2" width="18" height="15" rx="3" fill="currentColor" opacity=".5"/>
        <rect x="2" y="30" width="18" height="12" rx="3" fill="currentColor" opacity=".5"/>
        <rect x="24" y="21" width="18" height="21" rx="3" fill="currentColor" opacity=".7"/>
      </svg>
    ),
    desc: "Pinterest-style brick-wall grid. Images fill vertical gaps naturally using Masonry.js.",
  },
  {
    id: "vertical",
    label: "Vertical",
    icon: (
      <svg viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="2" width="32" height="11" rx="3" fill="currentColor" opacity=".9"/>
        <rect x="6" y="17" width="32" height="11" rx="3" fill="currentColor" opacity=".6"/>
        <rect x="6" y="32" width="32" height="11" rx="3" fill="currentColor" opacity=".35"/>
      </svg>
    ),
    desc: "Column-based vertical scrolling. Perfect for portrait and editorial galleries.",
  },
  {
    id: "horizontal",
    label: "Horizontal",
    icon: (
      <svg viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="8" width="12" height="28" rx="3" fill="currentColor" opacity=".9"/>
        <rect x="17" y="8" width="12" height="28" rx="3" fill="currentColor" opacity=".6"/>
        <rect x="32" y="8" width="12" height="28" rx="3" fill="currentColor" opacity=".35"/>
      </svg>
    ),
    desc: "Row-based horizontal scrolling. Great for landscape and panoramic images.",
  },
];

const themes = [
  { name: "Classic Dark Overlay", bg: "#18181b", swatch: "#18181b", tag: "dark" },
  { name: "Light Card",           bg: "#ffffff",  swatch: "#e8e8e8", tag: "light" },
  { name: "Polaroid Frame",       bg: "#fdf8ef",  swatch: "#f0e8d5", tag: "warm" },
  { name: "Minimal Borderless",   bg: "#f5f5f5",  swatch: "#d8d8d8", tag: "clean" },
  { name: "Neon Glow",            bg: "#040412",  swatch: "#040412", tag: "neon" },
];

const features = [
  { n: "01", title: "Guided Builder", body: "4-step wizard walks you from blank to live gallery. Info → Images → Customize → Preview." },
  { n: "02", title: "Media Library", body: "Native WordPress uploader integration. Pick images without leaving the admin." },
  { n: "03", title: "Live Preview",  body: "Adjust height, width, gap. Drag to reorder. Resize from corners before saving." },
  { n: "04", title: "Typography",    body: "Font size, weight, decoration, alignment & text shadow — individually per element." },
  { n: "05", title: "Color Engine",  body: "Solid colors or CSS gradients (from / to / angle) applied to any text element." },
  { n: "06", title: "Scoped CSS",    body: "Styles namespaced per gallery ID. Multiple galleries on one page never conflict." },
];

const stack = [
  { label: "PHP",        detail: "7.4+" },
  { label: "WordPress",  detail: "5.8+" },
  { label: "jQuery UI",  detail: "Sortable & Resizable" },
  { label: "Masonry.js", detail: "+ imagesLoaded" },
  { label: "AJAX",       detail: "Frontend saves" },
  { label: "GPL v2",     detail: "Open source" },
];



export default function MasonryGalleryPro() {
  const [activeLayout, setActiveLayout] = useState(0);
  const [activeTheme,  setActiveTheme]  = useState(0);
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const copy = () => {
    navigator.clipboard.writeText('[masonry_gallery id="1"]');
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.root}>

      {/* ─── HERO ─────────────────────────────────────────── */}
      <section className={styles.hero}>
        <div className={styles.heroAccentBar} />

        <div className={styles.heroContent}>
          <div className={styles.heroLeft}>
            <span className={styles.pluginBadge}>
              WordPress Plugin
            </span>

            <h1 className={styles.heroTitle}>
              Masonry<br/>
              <span className={styles.heroTitleAccent}>Gallery</span>
              <span className={styles.heroPro}> Pro</span>
            </h1>

            <p className={styles.heroDesc}>
              Create and manage beautiful image galleries from the WP admin panel — shortcode-embedded, zero coding required.
            </p>

            <div className={styles.embedRow}>
              <span className={styles.embedLabel}>Embed shortcode</span>
              <div className={styles.embedBox}>
                <code className={styles.embedCode}>[masonry_gallery id=&quot;1&quot;]</code>
                <button className={styles.copyButton} onClick={copy}>
                  {copied ? "✓ Copied!" : "Copy"}
                </button>
              </div>
            </div>

            <div className={styles.heroStats}>
              <div className={styles.heroStat}>
                <strong>5</strong>
                <span>Themes</span>
              </div>
              <div className={styles.heroStatDiv} />
              <div className={styles.heroStat}>
                <strong>3</strong>
                <span>Layouts</span>
              </div>
              <div className={styles.heroStatDiv} />
              <div className={styles.heroStat}>
                <strong>4</strong>
                <span>Builder steps</span>
              </div>
            </div>
          </div>

          <div className={styles.heroRight}>
            <img src={heroImg} alt="Masonry Gallery Pro preview" className={styles.heroImg} />
            {/* <div className={styles.floatTag1}>Masonry.js</div>
            <div className={styles.floatTag2}>Live Preview</div> */}
          </div>
        </div>

        <div className={styles.heroBottom}>
          <div className={styles.heroBottomGrid}>
            {["PHP 7.4+","WP 5.8+","GPL v2","jQuery UI","AJAX","Shortcode API"].map((t,i)=>(
              <span key={i} className={styles.heroBottomTag}>{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SCREENSHOTS ─────────────────────────────────── */}
      <section className={styles.section}>
        <div className={styles.sectionMeta}>
          <span className={styles.sectionN}>00</span>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>Plugin Screenshots</h2>
            <p className={styles.sectionSub}>Drop your actual screenshots into these slots.</p>
          </div>
        </div>

        <div className={styles.ssGrid}>
          <figure className={styles.ssCard}>
            <div className={styles.ssFrame}>
              <img src={ss1} alt="Gallery Dashboard" className={styles.ssImg} />
            </div>
          </figure>
          <figure className={styles.ssCard}>
            <div className={styles.ssFrame}>
              <img src={ss2} alt="Step 1 — Info" className={styles.ssImg} />
            </div>
          </figure>
          <figure className={styles.ssCard}>
            <div className={styles.ssFrame}>
              <img src={ss3} alt="Step 2 — Images" className={styles.ssImg} />
            </div>
          </figure>
          <figure className={styles.ssCard}>
            <div className={styles.ssFrame}>
              <img src={ss4} alt="Step 3 — Customize" className={styles.ssImg} />
            </div>
          </figure>
          <figure className={styles.ssCard}>
            <div className={styles.ssFrame}>
              <img src={ss5} alt="Step 3 — Customize" className={styles.ssImg} />
            </div>
          </figure>
          
          
        </div>
      </section>

      {/* ─── LAYOUTS ─────────────────────────────────────── */}
      <section className={`${styles.section} ${styles.sectionTinted}`}>
        <div className={styles.sectionMeta}>
          <span className={styles.sectionN}>01</span>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>Gallery Layouts</h2>
            <p className={styles.sectionSub}>Three distinct display modes for any content type.</p>
          </div>
        </div>

        <div className={styles.layoutRow}>
          {layouts.map((l, i) => (
            <button
              key={i}
              onClick={() => setActiveLayout(i)}
              className={`${styles.layoutCard} ${activeLayout === i ? styles.layoutCardOn : ""}`}
            >
              <div className={styles.layoutIconWrap}>{l.icon}</div>
              <h3 className={styles.layoutName}>{l.label}</h3>
              <p className={styles.layoutBody}>{l.desc}</p>
              <div className={styles.layoutIndicator}>
                {activeLayout === i ? "Active" : "Select"}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ─── THEMES ──────────────────────────────────────── */}
      {/* <section className={styles.section}>
        <div className={styles.sectionMeta}>
          <span className={styles.sectionN}>02</span>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>5 Built-in Themes</h2>
            <p className={styles.sectionSub}>One-click switching with full per-theme customisation.</p>
          </div>
        </div>

        <div className={styles.themeTabRow}>
          {themes.map((t, i) => (
            <button
              key={i}
              onClick={() => setActiveTheme(i)}
              className={`${styles.themeTab} ${activeTheme === i ? styles.themeTabOn : ""}`}
            >
              <span
                className={styles.themeTabSwatch}
                style={{
                  background: t.swatch,
                  outline: (t.tag === "neon") ? "1.5px solid #00ffe7" : (t.tag==="light"||t.tag==="clean"||t.tag==="warm") ? "1.5px solid #d0d0d0" : "none"
                }}
              />
              {t.name}
            </button>
          ))}
        </div>

        <div
          className={styles.themeCanvas}
          style={{ background: themes[activeTheme].bg }}
          data-tag={themes[activeTheme].tag}
        >
          <div className={styles.themeCanvasGrid}>
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className={`${styles.themeCanvasCell} ${styles[`tcc${i}`]}`}
                style={{
                  background: themes[activeTheme].tag === "neon"
                    ? "rgba(0,255,231,0.07)"
                    : themes[activeTheme].tag === "dark"
                    ? "rgba(255,255,255,0.07)"
                    : "rgba(0,0,0,0.06)",
                  borderColor: themes[activeTheme].tag === "neon"
                    ? "rgba(0,255,231,0.18)"
                    : themes[activeTheme].tag === "dark"
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.08)",
                }}
              >
                <div
                  className={styles.themeCanvasBar}
                  style={{
                    background: themes[activeTheme].tag === "neon" ? "#00ffe7"
                      : themes[activeTheme].tag === "dark" ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.18)"
                  }}
                />
              </div>
            ))}
          </div>
          <div
            className={styles.themeCanvasLabel}
            style={{
              color: themes[activeTheme].tag === "neon" ? "#00ffe7"
                : themes[activeTheme].tag === "dark" ? "rgba(255,255,255,0.3)"
                : "rgba(0,0,0,0.25)"
            }}
          >
            {themes[activeTheme].name}
          </div>
        </div>
      </section> */}

      {/* ─── FEATURES ────────────────────────────────────── */}
      <section className={`${styles.section} ${styles.sectionTinted}`}>
        <div className={styles.sectionMeta}>
          <span className={styles.sectionN}>03</span>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>Key Features</h2>
            <p className={styles.sectionSub}>Everything you need — nothing you don't.</p>
          </div>
        </div>

        <div className={styles.featGrid}>
          {features.map((f, i) => (
            <div key={i} className={styles.featCard}>
              <span className={styles.featN}>{f.n}</span>
              <h3 className={styles.featTitle}>{f.title}</h3>
              <p className={styles.featBody}>{f.body}</p>
              <div className={styles.featAccentLine} />
            </div>
          ))}
        </div>
      </section>

      {/* ─── BUILDER FLOW ────────────────────────────────── */}
      <section className={styles.section}>
        <div className={styles.sectionMeta}>
          <span className={styles.sectionN}>04</span>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>4-Step Builder</h2>
            <p className={styles.sectionSub}>Guided from blank canvas to live embed.</p>
          </div>
        </div>

        <div className={styles.flowTrack}>
          {[
            { step:"01", title:"Info",      body:"Name your gallery, set title & description." },
            { step:"02", title:"Images",    body:"Select from WordPress Media Library." },
            { step:"03", title:"Customize", body:"Apply themes, colors, typography, custom CSS." },
            { step:"04", title:"Preview",   body:"Live preview, then save and embed." },
          ].map((s, i, arr) => (
            <div key={i} className={styles.flowItem}>
              <div className={styles.flowBullet}>
                <span>{s.step}</span>
                {i < arr.length - 1 && <div className={styles.flowLine}/>}
              </div>
              <div className={styles.flowText}>
                <h4 className={styles.flowTitle}>{s.title}</h4>
                <p className={styles.flowBody}>{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── TECH STACK ──────────────────────────────────── */}
      <section className={`${styles.section} ${styles.sectionBlue}`}>
        <div className={styles.sectionMeta}>
          <span className={`${styles.sectionN} ${styles.sectionNWhite}`}>05</span>
          <div className={styles.sectionHead}>
            <h2 className={`${styles.sectionTitle} ${styles.sectionTitleWhite}`}>Tech Stack</h2>
            <p className={`${styles.sectionSub} ${styles.sectionSubWhite}`}>Solid WordPress fundamentals under the hood.</p>
          </div>
        </div>

        <div className={styles.stackGrid}>
          {stack.map((t, i) => (
            <div key={i} className={styles.stackCard}>
              <span className={styles.stackLabel}>{t.label}</span>
              <span className={styles.stackDetail}>{t.detail}</span>
            </div>
          ))}
        </div>

        <div className={styles.dbPill}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
          Custom DB table: <code>mgp_galleries</code> — with auto-migration support
        </div>
      </section>

      </div>
      <Footer />
    </>
  );
}